import React, { FC, useMemo } from 'react';

import './Recurring.css';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { EndType, PaymentLabel } from '../../@types/types';
import { GiftIcon } from '../../assets/svg/GiftIcon';
import { RecurringIcon } from '../../assets/svg/RecurringIcon';
import { dateInputFormat, frequencies, FrequencyName, GiftType } from '../../shared/constants';
import { isModifierKey, isDigit, getNextFirstOrFifteenth, getDisplayFormatOfDate } from '../../shared/utils';
import dayjs from '../../utils/dayjs-config';
import { SelectField, SelectFieldProps, TextField, TextFieldProps } from '../fields';

type RecurringSelectField = Omit<SelectFieldProps, 'id' | 'title'> & { title?: string };
type RecurringTextField = Omit<TextFieldProps, 'id' | 'title'> & { title?: string };

declare const IS_STORYBOOK: boolean;

export type RecurringProps = {
	giftType: GiftType;
	paymentLabel: PaymentLabel;
	endType: EndType;
	giftTypeField: React.InputHTMLAttributes<HTMLInputElement>;
	startDate?: string;
	startField: RecurringTextField;
	frequency?: FrequencyName;
	frequencyField: RecurringSelectField;
	endTypeField: RecurringSelectField;
	occurrencesField: RecurringTextField;
	endDateField: RecurringTextField;
	showEndFields: boolean;
};

const MAX_OCCURRENCES_CHAR_LENGTH = 4;

export const Recurring: FC<RecurringProps> = ({
	paymentLabel,
	endType,
	giftType,
	startDate,
	frequency,
	frequencyField,
	startField,
	occurrencesField,
	endDateField,
	endTypeField,
	giftTypeField,
	showEndFields,
}) => {
	const { errorMessage: startFieldError, title: startFieldTitle, ...startFieldPropsRest } = startField;
	const { errorMessage: occurrencesFieldError, ...occurrencesFieldPropsRest } = occurrencesField;
	const { errorMessage: endDateFieldError, ...endDateFieldPropsRest } = endDateField;
	const { errorMessage: frequencyFieldError, ...frequencyFieldPropsRest } = frequencyField;
	const { errorMessage: endTypeFieldError, ...endTypeFieldRest } = endTypeField;

	const { t: translate } = useTranslation();
	const isOnetime = giftType === GiftType.ONE_TIME;

	const frequencyInfoMessage = useMemo(() => {
		const actualStartDate = startDate
			? getNextFirstOrFifteenth(dayjs(startDate).format(dateInputFormat))
			: undefined;
		return frequency === frequencies.FirstAndFifteenth.name
			? translate('info.firstAndFifteenth', {
					startDate: getDisplayFormatOfDate(actualStartDate),
			  })
			: undefined;
	}, [startDate, frequency]);

	return (
		<div className="recurring-container">
			<div aria-label="Gift type selection" className="recurring-toggle" role="radiogroup">
				<label
					className={clsx('recurring-toggle-right', isOnetime && 'recurring-toggle-selected')}
					htmlFor="recurring-toggle-once"
				>
					<span className="recurring-toggle-icon">
						<GiftIcon width="30" />
					</span>
					{translate('recurring.oneTime', { label: paymentLabel.VerbSentenceCase })}
					<input
						checked={isOnetime}
						className="recurring-toggle-input"
						id="recurring-toggle-once"
						name="recurring-toggle"
						type="radio"
						{...giftTypeField}
						value={GiftType.ONE_TIME}
					/>
				</label>
				<label
					className={clsx('recurring-toggle-left', !isOnetime && 'recurring-toggle-selected')}
					htmlFor="recurring-toggle-multiple"
				>
					<span className="recurring-toggle-icon">
						<RecurringIcon width="30" />
					</span>
					{translate('recurring.recurring')}
					<input
						checked={!isOnetime}
						className="recurring-toggle-input"
						id="recurring-toggle-multiple"
						name="recurring-toggle"
						type="radio"
						{...giftTypeField}
						value={GiftType.RECURRING}
					/>
				</label>
			</div>
			<div
				className={clsx('recurring-fields', isOnetime ? 'recurring-fields-hidden' : 'recurring-fields-showing')}
			>
				<SelectField
					{...frequencyFieldPropsRest}
					acceptanceTestTargetId="recurring-field-frequency"
					errorMessage={frequencyFieldError}
					id="recurring-field-frequency"
					invalid={Boolean(frequencyFieldError)}
					title={translate('recurring.frequency')}
				/>
				<TextField
					{...startFieldPropsRest}
					acceptanceTestTargetId="recurring-field-start"
					data-chromatic={IS_STORYBOOK ? 'ignore' : undefined}
					errorMessage={startFieldError}
					id="recurring-field-start"
					infoMessage={frequencyInfoMessage}
					invalid={Boolean(startFieldError)}
					title={startFieldTitle || translate('recurring.starting')}
					type="date"
				/>

				{showEndFields && (
					<>
						<SelectField
							{...endTypeFieldRest}
							acceptanceTestTargetId="recurring-field-end"
							errorMessage={endTypeFieldError}
							id="recurring-field-endType"
							invalid={Boolean(endTypeFieldError)}
							title={translate('recurring.ending')}
							type="date"
						/>

						{endType === EndType.Date && (
							<TextField
								{...endDateFieldPropsRest}
								acceptanceTestTargetId="recurring-field-endDate"
								disabled={endType !== EndType.Date}
								errorMessage={endDateFieldError}
								id="recurring-field-endDate"
								invalid={Boolean(endDateFieldError)}
								title={translate('recurring.endDate')}
								type="date"
								required
							/>
						)}

						{endType === EndType.Occurrences && (
							<TextField
								{...occurrencesFieldPropsRest}
								acceptanceTestTargetId="recurring-field-occurrences"
								errorMessage={occurrencesFieldError}
								id="recurring-field-occurrences"
								invalid={Boolean(occurrencesFieldError)}
								placeholder="e.g. 1-1000"
								spellCheck={false}
								title={translate('recurring.occurrences', { label: paymentLabel.NounPluralLowerCase })}
								type="number"
								required
								onKeyDown={e => {
									if (!isDigit(e.key) && !isModifierKey(e.key)) {
										e.preventDefault();
										return;
									}

									if (isDigit(e.key) && e.currentTarget.value.length >= MAX_OCCURRENCES_CHAR_LENGTH) {
										e.preventDefault();
									}
								}}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};
